import { slugify, upcoming } from './events';
import type { Event } from '../types';

export type LeagueId = 'nfl' | 'mlb' | 'nhl' | 'nba' | 'mls' | 'ncaa_football' | 'ncaa_basketball' | 'wwe';

export const LEAGUES: Record<LeagueId, { name: string; long: string; sport: string }> = {
  nfl: { name: 'NFL', long: 'National Football League', sport: 'Football' },
  mlb: { name: 'MLB', long: 'Major League Baseball', sport: 'Baseball' },
  nhl: { name: 'NHL', long: 'National Hockey League', sport: 'Hockey' },
  nba: { name: 'NBA', long: 'National Basketball Association', sport: 'Basketball' },
  mls: { name: 'MLS', long: 'Major League Soccer', sport: 'Soccer' },
  ncaa_football: { name: 'NCAA Football', long: 'College football', sport: 'Football' },
  ncaa_basketball: { name: 'NCAA Basketball', long: 'College basketball', sport: 'Basketball' },
  wwe: { name: 'WWE', long: 'World Wrestling Entertainment', sport: 'Wrestling' },
};

export const LEAGUE_IDS = Object.keys(LEAGUES) as LeagueId[];

/**
 * Which league an event belongs to.
 *
 * The catalog has no league field — only `teams` (short names) and a venue.
 * Short names collide ACROSS leagues: Rangers (Texas/NY), Cardinals
 * (St. Louis/Arizona), Giants (SF/NY), Panthers (Carolina/Florida), Jets
 * (NY/Winnipeg) and Kings (LA/Sacramento) each name two different clubs. So
 * the name alone cannot decide.
 *
 * The home BUILDING can: a ballpark is never a football stadium, so an
 * outdoor event's venue fixes its league outright. Indoor arenas are shared
 * (TD Garden hosts the Bruins and the Celtics), so there we fall back to the
 * teams — and every real matchup names at least one club that belongs to
 * exactly one of the two leagues.
 */
const MLB_VENUES = new Set([
  'UNIQLO Field at Dodger Stadium',
  'Wrigley Field',
  'Fenway Park',
  'Citizens Bank Park',
  'Petco Park',
  'T-Mobile Park',
  'Truist Park',
  'Oracle Park',
  'Rate Field',
  'Globe Life Field',
  'Great American Ball Park',
  'Progressive Field',
  'Oriole Park at Camden Yards',
  'Busch Stadium',
  'PNC Park',
  'Comerica Park',
  'Target Field',
  'Coors Field',
  'Chase Field',
  'loanDepot park',
  'Daikin Park',
  'American Family Field',
  'Kauffman Stadium',
]);

const NFL_VENUES = new Set([
  'Allegiant Stadium',
  'Lumen Field',
  'Soldier Field',
  'Nissan Stadium - 1 Titans Way',
  'Lincoln Financial Field',
  'Caesars Superdome',
  'Acrisure Stadium',
  'Lambeau Field',
  'Northwest Stadium',
  'AT&T Stadium',
  'GEHA Field at Arrowhead Stadium',
  'Mercedes-Benz Stadium',
  'Empower Field at Mile High',
  'Reliant Stadium',
  'Paycor Stadium',
  'Ford Field',
  'U.S. Bank Stadium',
  'Bank of America Stadium',
  'Raymond James Stadium',
  'Huntington Bank Field',
  'M&T Bank Stadium',
  'Lucas Oil Stadium',
  'EverBank Stadium',
]);

const MLS_VENUES = new Set(['ScottsMiracle-Gro Field']);

/** Clubs that belong to exactly one of the two arena leagues (Kings is in
 *  neither list on purpose — it names an NHL and an NBA club). */
const NHL_ONLY = new Set([
  'Mammoth', 'Avalanche', 'Blackhawks', 'Maple Leafs', 'Stars', 'Blues', 'Wild', 'Sharks', 'Bruins',
  'Capitals', 'Flyers', 'Senators', 'Predators', 'Lightning', 'Hurricanes', 'Penguins', 'Rangers',
  'Islanders', 'Devils', 'Jets', 'Golden Knights', 'Kraken', 'Canucks', 'Flames', 'Red Wings',
  'Blue Jackets', 'Canadiens', 'Ducks', 'Sabres', 'Panthers', 'Oilers',
]);

const NBA_ONLY = new Set([
  'Warriors', 'Lakers', 'Trail Blazers', 'Celtics', '76ers', 'Spurs', 'Suns', 'Hawks', 'Grizzlies',
  'Thunder', 'Magic', 'Cavaliers', 'Heat', 'Hornets', 'Knicks', 'Nets', 'Bucks', 'Pacers', 'Pistons',
  'Raptors', 'Wizards', 'Jazz', 'Nuggets', 'Timberwolves', 'Pelicans', 'Rockets', 'Mavericks', 'Clippers',
]);

export const leagueOf = (e: Pick<Event, 'category' | 'venue' | 'teams' | 'kind'>): LeagueId | null => {
  if (e.category !== 'Sports') return null;
  if (e.kind && (LEAGUE_IDS as string[]).includes(e.kind)) return e.kind as LeagueId;
  if (MLB_VENUES.has(e.venue)) return 'mlb';
  if (NFL_VENUES.has(e.venue)) return 'nfl';
  if (MLS_VENUES.has(e.venue)) return 'mls';
  const teams = e.teams ?? [];
  if (teams.some((t) => NHL_ONLY.has(t))) return 'nhl';
  if (teams.some((t) => NBA_ONLY.has(t))) return 'nba';
  return null;
};

export const leagueBySlug = (slug?: string): LeagueId | null => {
  const s = (slug ?? '').toLowerCase();
  return (LEAGUE_IDS as string[]).includes(s) ? (s as LeagueId) : null;
};

export const eventsByLeague = (id: LeagueId, list: Event[] = upcoming()): Event[] => list.filter((e) => leagueOf(e) === id);

/** Leagues with enough dates in a list to deserve a section, busiest first.
 *  (The header menu uses the build-time counts in nav.json instead.) */
export const activeLeagues = (list: Event[], min = 8): { id: LeagueId; name: string; count: number }[] =>
  LEAGUE_IDS.map((id) => ({ id, name: LEAGUES[id].name, count: eventsByLeague(id, list).length }))
    .filter((l) => l.count >= min)
    .sort((a, b) => b.count - a.count);

/** The clubs playing in a league, busiest first — the second level of the
 *  Sports nav (league → club), each linking to a page that exists. */
export const teamsInLeague = (id: LeagueId, list: Event[] = upcoming()): { name: string; slug: string; count: number }[] => {
  const counts = new Map<string, number>();
  for (const e of eventsByLeague(id, list)) for (const t of e.teams ?? []) counts.set(t, (counts.get(t) ?? 0) + 1);
  return [...counts.entries()]
    .map(([name, count]) => ({ name, slug: slugify(name), count }))
    .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
};
